// hooks/api/alumni.queries.ts
import { notifyError, notifySuccess } from "@/lib/notification";
import * as alumniService from "@/services/alumni.service";
import {
  AlumniCreateType,
  AlumniParamsType,
  AlumniType,
} from "@/services/alumni.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";

export const alumniKey = {
  all: ["alumni"] as const,
  lists: (filters: AlumniParamsType) =>
    [...alumniKey.all, "lists", filters] as const,
  detail: (id: string) => [...alumniKey.all, "detail", id] as const,
};

export const useAlumniQuery = (filters: AlumniParamsType) =>
  useQuery({
    queryKey: alumniKey.lists(filters),
    queryFn: () => alumniService.fetchAllAlumni(filters),
    placeholderData: (prev) => prev,
  });

export const useAlumniByIdQuery = (id: string, enabled = true) =>
  useQuery({
    queryKey: alumniKey.detail(id),
    queryFn: () => alumniService.fetchAlumniById(id),
    enabled: enabled && !!id,
  });

export const useCreateAlumniMutation = (filters: AlumniParamsType) => {
  const queryClient = useQueryClient();
  return useMutation<AlumniType, AxiosError, AlumniCreateType>({
    mutationFn: (data) => alumniService.createAlumni(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: alumniKey.lists(filters) });
      notifySuccess("Alumni added successfully");
    },
    onError: (error: any) =>
      notifyError(error?.response?.data?.message || "Failed to add alumni"),
  });
};

export const useUpdateAlumniMutation = (filters: AlumniParamsType) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ _id, data }: { _id: string; data: AlumniCreateType }) =>
      alumniService.updateAlumni(_id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: alumniKey.lists(filters) });
      queryClient.invalidateQueries({
        queryKey: alumniKey.detail(variables._id),
      });
      notifySuccess("Alumni updated successfully");
    },
    onError: (error: any) =>
      notifyError(error?.response?.data?.message || "Failed to update alumni"),
  });
};

export const useDeleteAlumniMutation = (filters: AlumniParamsType) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (_id: string) => alumniService.deleteAlumni(_id),
    onSuccess: (_, _id) => {
      queryClient.invalidateQueries({ queryKey: alumniKey.lists(filters) });
      queryClient.removeQueries({ queryKey: alumniKey.detail(_id) });
      notifySuccess("Alumni deleted successfully");
    },
    onError: (error: any) =>
      notifyError(error?.response?.data?.message || "Failed to delete alumni"),
  });
};
